import { useMemo } from 'react';
import { useCompany } from '../context/CompanyContext';

/**
 * Returns the company's currency code (e.g. 'LKR', 'USD', 'EUR').
 * Reads from CompanyContext — fetched once on app start, no extra API calls.
 */
export const useCurrency = (): string => {
  return useCompany().currencyCode;
};

/**
 * Returns a pre-built number → formatted-currency-string function for the
 * company's currency.  Re-creates only when the currency changes.
 *
 * Usage:
 *   const fmt = useCurrencyFormat();
 *   fmt(1234.5)  →  "LKR 1,234.50"  (or "$1,234.50" etc.)
 */
export const useCurrencyFormat = (): ((n: number) => string) => {
  return useMemo(
    () => (n: number) =>
      new Intl.NumberFormat(undefined, {
        style: 'decimal',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(n),
    [],
  );
};

/**
 * Synchronous helper for non-hook contexts (e.g. module-level formatters).
 * Reads from localStorage where CompanyProvider persists the currency.
 */
export const getCachedCurrency = (): string =>
  localStorage.getItem('company_currency_code') || 'USD';
